import { useEffect, useState } from "react"
import { IOrganisation } from "@typed/api/responses/Organisation"
import { loadBytes } from "@services/pdf/assetLoader"
import { hardcodedBackCovers } from "@services/pdf/constants"
import { IOrgAttachment } from "@services/pdf/types"
import { fetchOrgAttachments } from "@services/api/implementations/rest/orgAttachments"

export interface IBuilderAssets {
	loading: boolean
	coverTemplate: Uint8Array | null
	detailsTemplate: Uint8Array | null
	backCoverBytes: Uint8Array | null
	attachments: IOrgAttachment[]
	/** iOS: hasBrand = org details template exists — gates the Branded Pages toggle */
	hasBrand: boolean
}

const pendingAssets: IBuilderAssets = {
	loading: true,
	coverTemplate: null,
	detailsTemplate: null,
	backCoverBytes: null,
	attachments: [],
	hasBrand: false,
}

/**
 * Pre-loads the org PDF assets before the customise screen shows
 * (iOS MSCreatePDF downloads templates before pushing WorkPDFCustomPage).
 */
export function useBuilderAssets(org: IOrganisation | null, selectedArtists: string[]): IBuilderAssets {
	const artistsKey = selectedArtists.join("\u0000") // NUL-joined: artist names contain spaces
	// Loaded assets are tagged with the inputs they were loaded FOR. When the
	// org changes (e.g. it arrives after the overlay opened), the key stops
	// matching and this hook reports loading:true in the SAME render —
	// otherwise the builder would mount with the previous (null-org) assets
	// and freeze them into its initial state, exporting an unbranded PDF.
	const assetsKey = `${org?.uid ?? ""}|${artistsKey}`
	const [loaded, setLoaded] = useState<{ key: string; assets: IBuilderAssets } | null>(null)

	useEffect(() => {
		let cancelled = false

		const load = async () => {
			const [coverTemplate, detailsTemplate, backCoverBytes, allAttachments] = await Promise.all([
				org?.pdf_cover_url ? loadBytes(org.pdf_cover_url) : Promise.resolve(null),
				org?.pdf_tmpl_details_url ? loadBytes(org.pdf_tmpl_details_url) : Promise.resolve(null),
				org && hardcodedBackCovers[org.uid] ? loadBytes(hardcodedBackCovers[org.uid]) : Promise.resolve(null),
				fetchOrgAttachments(org?.pdf_layout_type ?? ""),
			])

			if (cancelled) {
				return
			}

			const artists = artistsKey === "" ? [] : artistsKey.split("\u0000")
			setLoaded({
				key: assetsKey,
				assets: {
					loading: false,
					coverTemplate,
					detailsTemplate,
					backCoverBytes,
					attachments: allAttachments.filter((attachment) => artists.includes(attachment.artist)),
					hasBrand: detailsTemplate !== null,
				},
			})
		}

		load()
		return () => {
			cancelled = true
		}
	}, [org, artistsKey, assetsKey])

	return loaded !== null && loaded.key === assetsKey ? loaded.assets : pendingAssets
}
